import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import {
  SUPPORTED_LANGUAGES,
  PRIVACY_POLICY_VERSION,
  getBundle,
  detectDefaultLocale,
} from "@/lib/signupI18n";
import { getContactCopy } from "@/components/ContactAccountFields";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal";

export default function GoogleConsent() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const initialLocale = user?.preferred_language && SUPPORTED_LANGUAGES.some((l) => l.code === user.preferred_language)
    ? user.preferred_language
    : detectDefaultLocale();
  const [locale, setLocale] = useState(initialLocale);
  const b = getBundle(locale);
  const contactT = getContactCopy(locale);

  const [policyOpened, setPolicyOpened] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [choice, setChoice] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const accepted = choice === "accept";
  const declined = choice === "decline";

  const chooseAccept = () => {
    setErr("");
    if (!policyOpened) {
      setErr(b.privacyMustReadFirst);
      return;
    }
    setChoice("accept");
  };

  const submit = async () => {
    if (!accepted || !ageConfirmed) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/accept-privacy-consent", {
        accepted: true,
        policy_version: PRIVACY_POLICY_VERSION,
        policy_language: locale,
        accepted_at: new Date().toISOString(),
        age_of_majority_confirmed: true,
      });
      toast.success({
        "pt-BR": "Consentimento registrado.",
        en: "Consent recorded.",
        "es-419": "Consentimiento registrado.",
        "fr-CA": "Consentement enregistré.",
      }[locale] || "Consent recorded.");
      const updated = await refresh();
      if (!updated?.profile_completed) navigate("/setup", { replace: true });
      else navigate("/", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex, locale));
    } finally {
      setBusy(false);
    }
  };

  const exitAccountCreation = async () => {
    await logout();
    window.location.assign("/login");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8 space-y-4" data-testid="google-consent-screen" data-locale={locale}>
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)]">English Job Interview Simulator</div>

        <div className="rounded-2xl border border-[color:var(--border-stone)] p-3 bg-[color:var(--bg-surface-hover)]">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[color:var(--text-primary)] mb-1">{b.langSelectorLabel}</span>
            <select
              value={locale}
              onChange={(e) => {
                setLocale(e.target.value);
                setPolicyOpened(false);
                setChoice("");
              }}
              className="w-full rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm"
              data-testid="consent-language"
            >
              {SUPPORTED_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
            </select>
          </label>
        </div>

        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid="google-consent-title">{b.privacySectionTitle}</h1>
        <p className="text-sm text-[color:var(--text-secondary)]">{contactT.contactPurpose}</p>
        {user && (
          <div className="rounded-xl border border-dashed border-[color:var(--border-stone)] p-2 text-[11px] text-[color:var(--text-secondary)]">
            <span className="font-mono break-all">{user.email}</span> · Google
          </div>
        )}

        <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${ageConfirmed ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--border-stone)]"}`}>
          <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]" data-testid="google-age-confirm" />
          <span className="text-xs leading-snug">{contactT.ageConfirm}</span>
        </label>

        <div className="rounded-2xl border border-[color:var(--border-stone)] p-4 space-y-3 bg-white" data-testid="google-consent-block">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold">{b.privacySectionTitle}</div>
          <div className="text-[11px] text-[color:var(--text-secondary)]">{b.policyVersionLabel}: <span className="font-mono">{PRIVACY_POLICY_VERSION}</span> · {locale}</div>
          <button type="button" onClick={() => { setPolicyModalOpen(true); setPolicyOpened(true); }} className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-xs font-medium" data-testid="open-privacy-policy">
            {b.privacyReviewCta}
          </button>
          <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${accepted ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--border-stone)]"}`}>
            <input type="checkbox" checked={accepted} onChange={chooseAccept} className="mt-0.5 h-4 w-4 accent-[color:var(--brand)]" data-testid="consent-optin" />
            <span className="text-xs leading-snug">{b.optInLabel}</span>
          </label>
          <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer ${declined ? "border-[color:var(--score-bad)] bg-[color:var(--score-bad)]/5" : "border-[color:var(--border-stone)]"}`}>
            <input type="checkbox" checked={declined} onChange={() => { setErr(""); setChoice("decline"); }} className="mt-0.5 h-4 w-4 accent-[color:var(--score-bad)]" />
            <span className="text-xs leading-snug">{b.optOutLabel}</span>
          </label>
          {declined && (
            <div className="rounded-xl bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-3 text-xs text-[color:var(--score-bad)]">
              {b.declineMessage}
              <button type="button" onClick={exitAccountCreation} className="block mt-2 underline">{b.declineExitCta}</button>
            </div>
          )}
        </div>

        {err && <div className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]">{err}</div>}

        <button type="button" onClick={submit} disabled={!accepted || !ageConfirmed || busy} className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40" data-testid="google-consent-submit">
          {busy ? b.submitBusy : b.submitCta}
        </button>
      </div>

      {policyModalOpen && <PrivacyPolicyModal locale={locale} onClose={() => setPolicyModalOpen(false)} />}
    </div>
  );
}
